import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  ChangeLog,
  Currency,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalAccountToClientAccountFormValues,
  ExternalAccountTopUpOption,
  ExternalAccountTopUpOptionFormValues,
  ExternalAccountWithIntegrationFormValues,
  ExternalOrganizationAccount,
} from '../types';
import {
  useBindExternalAccountToClientAccountMutation,
  useCreateCustomersExternalAccountMutation,
  useCreateCustomersExternalAccountWithIntegrationMutation,
  useLazyGetCustomersExternalAccountChangeLogsQuery,
  useLazyGetCustomersExternalAccountQuery,
  useLazyGetCustomersExternalAccountsListQuery,
  useLazyGetCustomersExternalAccountTopUpOptionsQuery,
  useLazyGetCustomersPspIntegrationsQuery,
  useLazyGetCustomersPspPartnersQuery,
  useLazyGetPspIntegrationsOrganizationsAccountsQuery,
  useUpdateCustomerExternalAccountCurrenciesMutation,
  useUpdateCustomersExternalAccountStatusMutation,
  useDeleteCustomerPaymentAccountsMutation,
  useUpdateCustomerPaymentsAccountsMutation,
  useCreateCustomerPaymentsAccountsMutation,
} from '../../../customers/store/services/customers';
import { useLazyGetIndividualAccountBalanceQuery } from '../../../individual/store/services/individual';
import {
  clearExternalAccountPanel,
  setOpenPayNotificationVisible,
} from '../../store/slices/external-integration';
import { GeneralInfo } from '../../../individual/types';
import { CorporateGeneralInfo } from '../../../corporate/types';
import { PaymentType } from '../../../../store/dictionaries/types';
import {
  OrganizationType,
  Balance,
  PspPartner,
  PspIntegration,
  ExternalPspPartner,
} from '../../types';

import ROUTES from '../../../../routes';

const defaultFormValues = {
  pspPartnerId: '',
  accountId: '',
  iban: '',
  paymentType: '',
  currencyId: [],
  status: 'enabled',
};

const useExternalAccountForm = ({
  externalAccountId,
  externalAccount,
  customerId,
  type,
  pspPartners,
  pspIntegrations,
  baseInformation,
  activePermissions,
  changeLogs,
  paymentTypes,
  dictionariesCurrencies,
  externalAccountTopUpOptions,
}: {
  externalAccountId?: string;
  externalAccount?: ExternalAccount;
  customerId?: string;
  type: OrganizationType;
  pspPartners?: PspPartner[];
  pspIntegrations?: PspIntegration[];
  baseInformation?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
  activePermissions?: PermissionEnum[];
  changeLogs?: ChangeLog[];
  paymentTypes?: PaymentType[];
  dictionariesCurrencies?: Currency[];
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<ExternalPspPartner>();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [isPrimeVault, setIsPrimeVault] = useState(false);
  const [openPaydKeelIntegration, setOpenPaydKeelIntegration] = useState<
    PspIntegration | undefined
  >(undefined);
  const [isKeel, setIsKeel] = useState(false);
  const [integrationType, setIntegrationType] = useState<
    'with-integration' | 'without-integration'
  >('without-integration');
  const [creationType, setCreationType] = useState<'create-new' | 'bind'>(
    'create-new',
  );
  const [bindOrganizationPopupVisible, setBindOrganizationPopupVisible] =
    useState(false);
  const [
    organizationPspExternalAccountsList,
    setOrganizationPspExternalAccountsList,
  ] = useState<ExternalOrganizationAccount[]>([]);
  const [
    selectedExternalOrganizationAccount,
    setSelectedExternalOrganizationAccount,
  ] = useState<ExternalOrganizationAccount | undefined>(undefined);
  const [bindIsNotAvailalbe, setBindIsNotAvailable] = useState(false);
  const [primeVaultExternalId, setPrimeVaultExternalId] = useState('');

  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<string[]>(
    [],
  );
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<
    'details' | 'top-up' | 'change-logs'
  >('details');
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [defaultTopUpOptions, setDefaultTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [topUpOptions, setTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [optionsErrors, setOptionsErrors] = useState<
    {
      id: string;
      currencyId?: string;
      paymentType?: string;
    }[]
  >([]);

  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [createExternalAccount, { isLoading, isSuccess }] =
    useCreateCustomersExternalAccountMutation();
  const [
    createExternalAccountWithIntegration,
    { isLoading: createInProgress, isSuccess: createIsSuccess },
  ] = useCreateCustomersExternalAccountWithIntegrationMutation();

  const [getPspPartners] = useLazyGetCustomersPspPartnersQuery();
  const [getExternalAccountsList] =
    useLazyGetCustomersExternalAccountsListQuery();
  const [
    getBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetIndividualAccountBalanceQuery();
  const [getPspIntegrations] = useLazyGetCustomersPspIntegrationsQuery();
  const [
    getOrganizationExternalAccounts,
    {
      isLoading: externalAccountsIsLoading,
      isFetching: externalAccountsIsFetching,
    },
  ] = useLazyGetPspIntegrationsOrganizationsAccountsQuery();
  const [
    bindExternalAccountToClientAccount,
    { isLoading: bindExternalInProgress, isSuccess: bindExternalIsSuccess },
  ] = useBindExternalAccountToClientAccountMutation();

  const [updateStatus, { isLoading: requestInProgress }] =
    useUpdateCustomersExternalAccountStatusMutation();
  const [
    getExternalAccount,
    {
      isLoading: externalAccountIsLoading,
      isFetching: externalAccountIsFetching,
    },
  ] = useLazyGetCustomersExternalAccountQuery();
  const [
    getExternalAccountTopUpOptions,
    { isLoading: topUpIsLoading, isFetching: topUpIsFetching },
  ] = useLazyGetCustomersExternalAccountTopUpOptionsQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersExternalAccountChangeLogsQuery();
  const [
    updateCustomerExternalAccountCurrencies,
    {
      isLoading: updateCustomerExternalAccountCurrenciesInProgress,
      isSuccess: updateCustomerExternalAccountCurrenciesIsSuccess,
    },
  ] = useUpdateCustomerExternalAccountCurrenciesMutation();
  const [
    deletePaymentAccounts,
    {
      isLoading: deletePaymentAccountsInProgress,
      isSuccess: deletePaymentAccountsIsSuccess,
    },
  ] = useDeleteCustomerPaymentAccountsMutation();
  const [
    createPaymentAccounts,
    {
      isLoading: createPaymentAccountsInProgress,
      isSuccess: createPaymentAccountsIsSuccess,
    },
  ] = useCreateCustomerPaymentsAccountsMutation();
  const [
    updatePaymentAccounts,
    {
      isLoading: updatePaymentAccountsInProgress,
      isSuccess: updatePaymentAccountsIsSuccess,
    },
  ] = useUpdateCustomerPaymentsAccountsMutation();

  const {
    reset,
    control,
    formState: { errors, isDirty },
    setError,
    handleSubmit,
    setValue,
    watch,
    getValues,
  } = useForm<ExternalAccountFormValues>({
    defaultValues: defaultFormValues,
  });

  const pspPartnerId = watch('pspPartnerId');
  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const accountId = watch('accountId');
  const caspId = watch('caspId');
  const status = watch('status');

  const prevIntegrationType = usePrevious(integrationType);
  const prevCaspId = usePrevious(caspId);
  const prevPaymentType = usePrevious(paymentType);
  const prevStatus = usePrevious(status);

  const isDirtyValue = externalAccountId
    ? (status && prevStatus && status !== prevStatus) ||
      !isEqual(topUpOptions, defaultTopUpOptions)
    : isDirty;

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  const deletePaymentAccountsAction = async () => {
    await deletePaymentAccounts(selectedOptionId);

    if (optionsErrors?.find((item) => item.id === selectedOptionId)) {
      setOptionsErrors(
        optionsErrors?.filter((item) => item.id !== selectedOptionId),
      );
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    if (!externalAccountId) {
      const body: ExternalAccountFormValues = {
        pspPartnerId: values.pspPartnerId,
        accountId: values.accountId,
        iban: values.iban,
        paymentType: values.paymentType,
        currencyId: values.currencyId,
        active: Boolean(values.status === 'enabled'),
        currenciesIds: values.currencyId,
      };

      if (values?.caspId) {
        body.caspId = values.caspId;
      }

      const response = (await createExternalAccount(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data?: ExternalAccount };
      };

      if (response) {
        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError,
          });
        } else {
          getExternalAccountsList(
            `?take=20&organizationId=${customerId}${
              search ? search.replace('?', '&') : ''
            }`,
          );
        }
      }
    } else {
      setOptionsErrors([]);

      const optionsForCreate: ExternalAccountTopUpOptionFormValues[] = [];
      const optionForUpdate: ExternalAccountTopUpOptionFormValues[] = [];
      const optionForWithErrors: ExternalAccountTopUpOptionFormValues[] = [];

      topUpOptions?.forEach((item) => {
        if (!item?.paymentType || !item?.currencyId) {
          optionForWithErrors.push(item);
        }

        if (item?.id?.includes('option')) {
          optionsForCreate.push(item);
        }

        if (!item?.id?.includes('option')) {
          const currentDefaultOption = defaultTopUpOptions?.find(
            (defaultOption) => defaultOption?.id === item?.id,
          );

          if (currentDefaultOption && !isEqual(currentDefaultOption, item)) {
            optionForUpdate.push(item);
          }
        }
      });

      if (!optionForWithErrors?.length) {
        if (optionsForCreate?.length) {
          await Promise.all(
            optionsForCreate?.map(async (item) => {
              const body: ExternalAccountTopUpOptionFormValues = {
                paymentType: item?.paymentType,
                currencyId: item?.currencyId,
                bankAccountHolderName: item?.bankAccountHolderName,
                accountNumber: item?.accountNumber,
                bankAddress: item?.bankAddress,
                bic: item?.bic,
                iban: item?.iban,
                bankName: item?.bankName,
                pspIntegrationAccountId: externalAccount?.id,
              };

              if (item?.thirdPartyReference) {
                body.thirdPartyReference = item.thirdPartyReference;
              }

              if (
                item?.routingCodeEntries?.filter(
                  (code) => code?.routingCodeKey && code?.routingCodeValue,
                )?.length
              ) {
                body.routingCodeEntries = item?.routingCodeEntries
                  ?.filter(
                    (code) => code?.routingCodeKey && code?.routingCodeValue,
                  )
                  ?.map((code) => ({
                    routingCodeKey: code.routingCodeKey,
                    routingCodeValue: code.routingCodeValue,
                  }));
              }

              await createPaymentAccounts(body);
            }),
          );
        }

        if (optionForUpdate?.length) {
          await Promise.all(
            optionForUpdate?.map(async (item) => {
              const body: ExternalAccountTopUpOptionFormValues = {
                paymentType: item?.paymentType,
                currencyId: item?.currencyId,
                bankAccountHolderName: item?.bankAccountHolderName,
                accountNumber: item?.accountNumber,
                bankAddress: item?.bankAddress,
                bic: item?.bic,
                iban: item?.iban,
                bankName: item?.bankName,
              };

              if (item?.thirdPartyReference) {
                body.thirdPartyReference = item.thirdPartyReference;
              }

              if (
                item?.routingCodeEntries?.filter(
                  (code) => code.routingCodeKey && code.routingCodeValue,
                )?.length
              ) {
                body.routingCodeEntries = item?.routingCodeEntries
                  ?.filter(
                    (code) => code.routingCodeKey && code.routingCodeValue,
                  )
                  ?.map((code) => ({
                    routingCodeKey: code.routingCodeKey,
                    routingCodeValue: code.routingCodeValue,
                  }));
              }

              const currentDefaultTopUpOptions = defaultTopUpOptions?.find(
                (option) => option?.id === item?.id,
              );

              if (
                !item?.routingCodeEntries?.filter(
                  (code) => code.routingCodeKey && code.routingCodeValue,
                ) &&
                currentDefaultTopUpOptions &&
                currentDefaultTopUpOptions?.routingCodeEntries?.length
              ) {
                body.routingCodeEntries = null;
              }

              await updatePaymentAccounts({
                id: item?.id as string,
                body,
              });
            }),
          );
        }

        if (prevStatus !== values.status) {
          const response = (await updateStatus({
            ids: [externalAccountId],
            active: Boolean(values.status === 'enabled'),
          })) as { error?: ResponseError };
          if (response?.error) {
            responseErrorsResolver({
              errors: response?.error,
              setError: () => {},
              setGlobalError,
            });
          }
        }

        if (!isEqual(externalAccount?.currenciesIds, values.currencyId)) {
          const response = (await updateCustomerExternalAccountCurrencies({
            id: externalAccount?.id as string,
            currenciesIds: values.currencyId,
          })) as { error?: ResponseError };
          if (response?.error) {
            responseErrorsResolver({
              errors: response?.error,
              setError: () => {},
              setGlobalError,
            });
          }
        }
      } else {
        const optionErrors: {
          id: string;
          currencyId?: string;
          paymentType?: string;
        }[] = [];

        optionForWithErrors?.forEach((item) => {
          const body: {
            id: string;
            currencyId?: string;
            paymentType?: string;
          } = {
            id: item.id as string,
          };

          if (!item?.currencyId) {
            body.currencyId = 'Currency is required';
          }

          if (!item.paymentType) {
            body.paymentType = 'Payment Type is required';
          }

          optionErrors.push(body);
        });

        setOptionsErrors(optionErrors);
      }
    }
  });

  const onSubmitWithIntegration = handleSubmit(async (values) => {
    setGlobalError('');

    if (creationType === 'create-new') {
      const body: ExternalAccountWithIntegrationFormValues = {
        accountsIds: [values.accountId],
        integrationId: selectedPartner?.pspIntegrationId as string,
        currenciesIds: values.currencyId,
      };

      if (values.caspId) {
        body.caspId = values.caspId;
      }

      const response = (await createExternalAccountWithIntegration(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data?: ExternalAccount };
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError,
        });
      }
    } else if (creationType === 'bind') {
      const formValues = getValues();

      if (!formValues.caspId && isPrimeVault) {
        setError('caspId', {
          type: 'custom',
          message: 'CASP is required',
        });
      } else {
        const body: ExternalAccountToClientAccountFormValues = {
          pspPartnerId: formValues.pspPartnerId,
          accountId: formValues.accountId,
          externalId:
            isOpenPayd || isKeel
              ? (selectedExternalOrganizationAccount?.externalId as string)
              : primeVaultExternalId,
        };

        if (formValues.caspId) {
          body.caspId = formValues.caspId;
        }

        const response = await bindExternalAccountToClientAccount(body);

        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError,
          });
        }
      }
    }
  });

  const getOrganizationExternalAccountsAction = useCallback(
    async (pspIntegrationId: string, organizationId: string) => {
      const response = await getOrganizationExternalAccounts(
        `?pspIntegrationId=${pspIntegrationId}&organizationId=${organizationId}`,
      );

      if (response?.data?.data) {
        setOrganizationPspExternalAccountsList(response?.data?.data);
      }
    },
    [],
  );

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PSP_INTEGRATION_ACCOUNT&entityId=${externalAccountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [externalAccountId],
  );

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');

    reset(defaultFormValues);
    setOrganizationPspExternalAccountsList([]);
    setSelectedExternalOrganizationAccount(undefined);
    setPrimeVaultExternalId('');

    let link = '';

    if (type === OrganizationType.INDIVIDUAL) {
      link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST, {
        customerId: customerId || '',
      });
    } else {
      link = generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST, {
        customerId: customerId || '',
      });
    }

    if (externalAccountId) {
      dispatch(clearExternalAccountPanel());
    }

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (deletePaymentAccountsIsSuccess) {
      setDeleteConfirmationVisible(false);
      setSelectedOptionId('');
    }
  }, [deletePaymentAccountsIsSuccess]);

  useEffect(() => {
    if (externalAccountTopUpOptions) {
      const nextTopUpOptions = externalAccountTopUpOptions?.map((item) => ({
        id: item?.id,
        paymentType: item?.paymentType,
        currencyId: item?.currencyId,
        bankAccountHolderName: item?.bankAccountHolderName,
        accountNumber: item?.accountNumber,
        bankAddress: item?.bankAddress,
        bic: item?.bic,
        iban: item?.iban,
        bankName: item?.bankName,
        thirdPartyReference: item?.thirdPartyReference,
        pspIntegrationAccountId: item?.pspIntegrationAccountId,
        routingCodeEntries:
          item?.routingCodeEntries?.map((code) => ({
            id: uuid(),
            routingCodeKey: code?.routingCodeKey,
            routingCodeValue: code?.routingCodeValue,
          })) || [],
      }));

      setDefaultTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
      setTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
    }
  }, [externalAccountTopUpOptions]);

  useEffect(() => {
    if (pspPartnerId && pspPartners && pspIntegrations && !externalAccountId) {
      const currentPspPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId,
      );

      if (currentPspPartner) {
        const currentPspIntegration = pspIntegrations?.find(
          (item) => item.id === currentPspPartner?.pspIntegrationId,
        );

        if (currentPspIntegration) {
          if (currentPspIntegration.name === 'OPEN_PAYD') {
            setIsOpenPayd(true);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setPrimeVaultExternalId('');
            setIsPrimeVault(false);
            setIsKeel(false);
          } else if (currentPspIntegration.name === 'Keel') {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setPrimeVaultExternalId('');
            setIsPrimeVault(false);
            setIsKeel(true);
          } else if (currentPspIntegration.name === 'PRIME_VAULT_WALLET') {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(undefined);
            setIsPrimeVault(true);
            setIsKeel(false);
          } else {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(undefined);
            setIsPrimeVault(false);
            setPrimeVaultExternalId('');
            setIsKeel(false);
          }
        }
      }
    }
  }, [pspPartnerId, pspIntegrations, pspPartners, externalAccountId]);

  useEffect(() => {
    if (!pspIntegrations) {
      getPspIntegrations();
    }
  }, [pspIntegrations]);

  useEffect(() => {
    if (accountId && !externalAccountId) {
      getAccountsBalancesAction(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (externalAccount) {
      getAccountsBalancesAction(externalAccount?.account?.id);
    }
  }, [externalAccount]);

  useEffect(() => {
    if (
      !externalAccountId &&
      ((((!isPrimeVault && !isOpenPayd && !isKeel) ||
        (isPrimeVault && integrationType === 'without-integration') ||
        ((isOpenPayd || isKeel) &&
          integrationType === 'without-integration')) &&
        paymentType !== prevPaymentType) ||
        (isPrimeVault &&
          integrationType === 'with-integration' &&
          caspId !== prevCaspId))
    ) {
      setValue('currencyId', []);
    }
  }, [
    isPrimeVault,
    isOpenPayd,
    isKeel,
    integrationType,
    prevCaspId,
    prevPaymentType,
    caspId,
    paymentType,
    externalAccountId,
  ]);

  useEffect(() => {
    if (
      integrationType &&
      prevIntegrationType &&
      integrationType === 'without-integration' &&
      prevIntegrationType === 'with-integration' &&
      !externalAccountId
    ) {
      setSelectedExternalOrganizationAccount(undefined);
    }
  }, [prevIntegrationType, integrationType, externalAccountId]);

  useEffect(() => {
    if (
      !externalAccountId &&
      accountId &&
      openPaydKeelIntegration &&
      customerId &&
      integrationType === 'with-integration' &&
      creationType === 'bind' &&
      baseInformation &&
      baseInformation?.pspIntegrationOrganization
    ) {
      getOrganizationExternalAccountsAction(
        openPaydKeelIntegration.id,
        customerId,
      );
    }
  }, [
    accountId,
    integrationType,
    creationType,
    customerId,
    openPaydKeelIntegration,
    baseInformation,
    externalAccountId,
  ]);

  useEffect(() => {
    if (
      (isSuccess ||
        createIsSuccess ||
        bindExternalIsSuccess ||
        updateCustomerExternalAccountCurrenciesIsSuccess ||
        createPaymentAccountsIsSuccess ||
        updatePaymentAccountsIsSuccess) &&
      !createPaymentAccountsInProgress &&
      !updatePaymentAccountsInProgress
    ) {
      handleClose();

      if (createIsSuccess || bindExternalIsSuccess) {
        dispatch(setOpenPayNotificationVisible(true));
      }
    }
  }, [
    isSuccess,
    createIsSuccess,
    bindExternalIsSuccess,
    updateCustomerExternalAccountCurrenciesIsSuccess,
    createPaymentAccountsIsSuccess,
    updatePaymentAccountsIsSuccess,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
  ]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (!pspPartners) {
      getPspPartners();
    }
  }, []);

  useEffect(() => {
    if (externalAccountId) {
      getExternalAccount(externalAccountId);
    }
  }, [externalAccountId]);

  useEffect(() => {
    if (
      activeTab === 'top-up' &&
      externalAccountId &&
      externalAccount?.pspIntegrationPayment?.paymentsTypes?.length &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
      )
    ) {
      getExternalAccountTopUpOptions({
        id: externalAccountId,
      });
    }
  }, [
    activeTab,
    externalAccountId,
    externalAccount?.pspIntegrationPayment?.paymentsTypes,
  ]);

  useEffect(() => {
    if (
      activeTab === 'change-logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (externalAccount) {
      if (
        paymentTypes &&
        balances &&
        dictionariesCurrencies &&
        pspIntegrations
      ) {
        const types: string[] = [];

        externalAccount?.pspIntegrationPayment?.paymentsTypes?.forEach(
          (item) => {
            if (
              paymentTypes?.find((typeValue) => typeValue.paymentType === item)
            ) {
              types.push(
                paymentTypes?.find(
                  (typeValue) => typeValue.paymentType === item,
                )?.name as string,
              );
            }
          },
        );

        const currentPspIntegration = pspIntegrations?.find(
          (item) => item.id === externalAccount.pspPartner?.pspIntegrationId,
        );

        if (currentPspIntegration) {
          if (currentPspIntegration.name === 'OPEN_PAYD') {
            setIsOpenPayd(true);
          }
        }

        setAvailablePaymentTypes(types);
      }
    }
  }, [
    externalAccount,
    paymentTypes,
    balances,
    dictionariesCurrencies,
    pspIntegrations,
  ]);

  useEffect(() => {
    if (externalAccount && pspIntegrations) {
      const currentPspIntegration = pspIntegrations?.find(
        (item) => item.id === externalAccount?.pspPartner?.pspIntegrationId,
      );

      if (currentPspIntegration) {
        if (currentPspIntegration.name === 'OPEN_PAYD') {
          setIsOpenPayd(true);
          setOpenPaydKeelIntegration(currentPspIntegration);
        } else if (currentPspIntegration.name === 'Keel') {
          setIsKeel(true);
          setOpenPaydKeelIntegration(currentPspIntegration);
        } else if (currentPspIntegration.name === 'PRIME_VAULT_WALLET') {
          setIsPrimeVault(true);
          setOpenPaydKeelIntegration(undefined);
        } else {
          setIsOpenPayd(false);
          setIsPrimeVault(false);
          setOpenPaydKeelIntegration(undefined);
        }
      }
    }
  }, [externalAccount, pspIntegrations]);

  useEffect(() => {
    if (externalAccount) {
      reset({
        status: externalAccount.active ? 'enabled' : 'disabled',
        caspId: externalAccount?.caspId || '',
        currencyId: externalAccount.currenciesIds,
      });
    }
  }, [externalAccount]);

  return {
    externalAccountIsLoading,
    externalAccountIsFetching,
    selectedPartner,
    isOpenPayd,
    isKeel,
    integrationType,
    openPaydKeelIntegration,
    balances,
    paymentType,
    caspId,
    selectedExternalOrganizationAccount,
    setBindIsNotAvailable,
    isPrimeVault,
    creationType,
    accountId,
    pspPartnerId,
    control,
    errors,
    setSelectedPartner,
    setValue,
    setIsOpenPayd,
    setIsPrimeVault,
    setIntegrationType,
    setCreationType,
    setBindOrganizationPopupVisible,
    setPanelIsOpen,
    organizationPspExternalAccountsList,
    externalAccountsIsLoading,
    externalAccountsIsFetching,
    bindIsNotAvailalbe,
    primeVaultExternalId,
    setPrimeVaultExternalId,
    balancesIsLoading,
    currencyId,
    panelIsOpen,
    isDirty: isDirtyValue,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    isLoading,
    createInProgress,
    bindExternalInProgress,
    onSubmitWithIntegration,
    onSubmit,
    dirtyPopupVisible,
    bindOrganizationPopupVisible,
    setSelectedExternalOrganizationAccount,
    availablePaymentTypes,
    activeTab,
    setActiveTab,
    topUpIsLoading,
    topUpIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    balancesIsFetching,
    requestInProgress,
    updateCustomerExternalAccountCurrenciesInProgress,
    setIsKeel,

    topUpOptions,
    setTopUpOptions,
    setSelectedOptionId,
    setDeleteConfirmationVisible,
    optionsErrors,
    setOptionsErrors,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    deletePaymentAccountsAction,
    deletePaymentAccountsInProgress,
    deleteConfirmationVisible,
  };
};

export default useExternalAccountForm;
